/**
 * Contact Book Content
 *
 * Manages professional contacts and leads with category-based
 * and date-based views. Supports CRUD for contacts and categories.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Users,
  Plus,
  Search,
  Calendar,
  Tag,
  MoreVertical,
  Edit,
  Trash2,
  Settings,
  Loader2,
  X,
  FolderOpen,
  GripVertical,
  ChevronDown,
  Flag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/use-toast';
import {
  api,
  ContactDetail,
  ContactCategory,
  ContactPriority,
  ContactInput,
  ContactCategoryInput,
  ContactPriorityInput,
  CustomFieldDefinition,
} from '@/lib/api';
import { useArcheSettings } from '@/lib/hooks/useArcheSettings';
import { ARCHE_ID } from '../constants';
import { ContactBookSettings, DEFAULT_SETTINGS } from '../types';
import { groupContactsByDate } from '../utils';
import { ContactCard } from '../components/ContactCard';
import { ContactFormDialog } from '../components/ContactFormDialog';
import { CategoryDialog } from '../components/CategoryDialog';
import { DeleteConfirmDialog, EmptyTrashDialog } from '../components/ConfirmDialogs';
import { ManageCustomFieldsDialog } from '../CustomFieldsDialog';
import { DuplicatesDialog } from '../DuplicatesDialog';
import { ShareDialog } from '../ShareDialog';
import { ExportDialog } from '../ExportDialog';
import { ContactImportDialog } from '../ContactImportDialog';

// =============================================================================
// MAIN CONTENT
// =============================================================================

export function ContactBookContent() {
  const [settings, updateSettings] = useArcheSettings<ContactBookSettings>(
    ARCHE_ID,
    DEFAULT_SETTINGS,
  );

  // Data state
  const [contacts, setContacts] = useState<ContactDetail[]>([]);
  const [categories, setCategories] = useState<ContactCategory[]>([]);
  const [priorities, setPriorities] = useState<ContactPriority[]>([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [loading, setLoading] = useState(true);

  // UI state
  const [view, setView] = useState<'category' | 'date'>(settings.defaultView);
  const [search, setSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    settings.selectedCategoryId,
  );

  // Dialogs
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<ContactDetail | null>(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ContactCategory | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState<ContactDetail | null>(null);
  const [emptyTrashDialogOpen, setEmptyTrashDialogOpen] = useState(false);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [permanentDeletingContact, setPermanentDeletingContact] = useState<ContactDetail | null>(
    null,
  );
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<ContactCategory | null>(null);
  const [trashCount, setTrashCount] = useState(0);
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);
  const [prioritiesExpanded, setPrioritiesExpanded] = useState(false);
  const [selectedPriorityId, setSelectedPriorityId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);
  const [customFieldsOpen, setCustomFieldsOpen] = useState(false);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [sharingContact, setSharingContact] = useState<ContactDetail | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const isTrashView = selectedCategoryId === '__deleted__';

  // Fetch data
  const fetchContacts = useCallback(async () => {
    try {
      const result = await api.listContacts({
        categoryId: isTrashView ? undefined : selectedCategoryId || undefined,
        priorityId: selectedPriorityId || undefined,
        status: isTrashView ? 'deleted' : undefined,
        search: search || undefined,
        sortBy: settings.sortBy,
        sortDir: settings.sortDir,
        limit: 200,
      });
      setContacts(result.contacts);
      setTotalContacts(result.total);
    } catch (err) {
      toast({
        title: 'Failed to load contacts',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [
    selectedCategoryId,
    selectedPriorityId,
    isTrashView,
    search,
    settings.sortBy,
    settings.sortDir,
  ]);

  const fetchTrashCount = useCallback(async () => {
    try {
      const result = await api.getTrashCount();
      setTrashCount(result.count);
    } catch {
      // Non-critical, ignore
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const result = await api.listContactCategories();
      setCategories(result.categories);
    } catch (err) {
      toast({
        title: 'Failed to load categories',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, []);

  const fetchPriorities = useCallback(async () => {
    try {
      const result = await api.listContactPriorities();
      setPriorities(result.priorities);
    } catch (err) {
      toast({
        title: 'Failed to load priorities',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, []);

  const fetchCustomFields = useCallback(async () => {
    try {
      const result = await api.listCustomFields();
      setCustomFields(result.fields);
    } catch (err) {
      toast({
        title: 'Failed to load custom fields',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, []);

  // Initial load — only runs once on mount
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;
    const load = async () => {
      setLoading(true);
      await Promise.all([
        fetchContacts(),
        fetchCategories(),
        fetchPriorities(),
        fetchTrashCount(),
        fetchCustomFields(),
      ]);
      setLoading(false);
    };
    load();
  }, []);

  // Refetch contacts when filters change (search is debounced, others immediate)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(
      () => {
        fetchContacts();
      },
      search ? 300 : 0,
    );
    return () => clearTimeout(timer);
  }, [search, fetchContacts]);

  // Category selection
  const handleCategorySelect = useCallback(
    (catId: string | null) => {
      setSelectedCategoryId(catId);
      setSelectedPriorityId(null);
      updateSettings({ selectedCategoryId: catId });
    },
    [updateSettings],
  );

  // Priority selection
  const handlePrioritySelect = useCallback(
    (prioId: string | null) => {
      setSelectedPriorityId(prioId);
      setSelectedCategoryId(null);
      updateSettings({ selectedCategoryId: null });
    },
    [updateSettings],
  );

  // Contact CRUD
  const handleSaveContact = useCallback(
    async (data: ContactInput, contactId?: string) => {
      try {
        if (contactId) {
          await api.updateContact(contactId, data);
          toast({ description: 'Contact updated.' });
        } else {
          const now = new Date().toISOString();
          const createdEntry = {
            id: crypto.randomUUID(),
            text: 'Contact created',
            date: now,
            showDate: true,
          };
          const existing =
            (data.metadata?.contactHistory as {
              id: string;
              text: string;
              date: string;
              showDate?: boolean;
            }[]) || [];
          await api.createContact({
            ...data,
            metadata: { ...data.metadata, contactHistory: [...existing, createdEntry] },
          });
          toast({ description: 'Contact added.' });
        }
        await Promise.all([fetchContacts(), fetchCategories()]);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Operation failed';
        toast({ title: 'Error', description: message, variant: 'destructive' });
        throw err;
      }
    },
    [fetchContacts, fetchCategories],
  );

  const handleEditContact = useCallback((contact: ContactDetail) => {
    setEditingContact(contact);
    setContactDialogOpen(true);
  }, []);

  const handleShareContact = useCallback((contact: ContactDetail) => {
    setSharingContact(contact);
  }, []);

  const handleDeleteContact = useCallback((contact: ContactDetail) => {
    setDeletingContact(contact);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingContact) return;
    try {
      await api.deleteContact(deletingContact.id);
      toast({ description: 'Contact moved to Deleted Contacts.' });
      setDeleteDialogOpen(false);
      setDeletingContact(null);
      await Promise.all([fetchContacts(), fetchCategories(), fetchTrashCount()]);
    } catch (err) {
      toast({
        title: 'Failed to delete',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [deletingContact, fetchContacts, fetchCategories, fetchTrashCount]);

  const handleRestoreContact = useCallback(
    async (contact: ContactDetail) => {
      try {
        await api.restoreContact(contact.id);
        toast({
          description: `${[contact.firstName, contact.lastName].filter(Boolean).join(' ')} restored.`,
        });
        await Promise.all([fetchContacts(), fetchCategories(), fetchTrashCount()]);
      } catch (err) {
        toast({
          title: 'Failed to restore',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      }
    },
    [fetchContacts, fetchCategories, fetchTrashCount],
  );

  const handlePermanentDelete = useCallback((contact: ContactDetail) => {
    setPermanentDeletingContact(contact);
    setPermanentDeleteDialogOpen(true);
  }, []);

  const handleConfirmPermanentDelete = useCallback(async () => {
    if (!permanentDeletingContact) return;
    try {
      await api.permanentlyDeleteContact(permanentDeletingContact.id);
      toast({ description: 'Contact permanently deleted.' });
      setPermanentDeleteDialogOpen(false);
      setPermanentDeletingContact(null);
      await Promise.all([fetchContacts(), fetchTrashCount()]);
    } catch (err) {
      toast({
        title: 'Failed to delete',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [permanentDeletingContact, fetchContacts, fetchTrashCount]);

  const handleEmptyTrash = useCallback(async () => {
    try {
      const result = await api.emptyTrash();
      toast({ description: `${result.deletedCount} contact(s) permanently deleted.` });
      setEmptyTrashDialogOpen(false);
      await Promise.all([fetchContacts(), fetchTrashCount()]);
    } catch (err) {
      toast({
        title: 'Failed to empty trash',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [fetchContacts, fetchTrashCount]);

  const handleArchiveContact = useCallback(
    async (contact: ContactDetail) => {
      try {
        const archived = contact.status !== 'archived';
        await api.archiveContact(contact.id, archived);
        toast({ description: archived ? 'Contact archived.' : 'Contact restored.' });
        await fetchContacts();
      } catch (err) {
        toast({
          title: 'Failed to archive',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      }
    },
    [fetchContacts],
  );

  // Category CRUD
  const handleSaveCategory = useCallback(
    async (data: ContactCategoryInput, categoryId?: string) => {
      try {
        if (categoryId) {
          await api.updateContactCategory(categoryId, data);
          toast({ description: 'Category updated.' });
        } else {
          await api.createContactCategory(data);
          toast({ description: 'Category created.' });
        }
        await fetchCategories();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Operation failed';
        toast({ title: 'Error', description: message, variant: 'destructive' });
        throw err;
      }
    },
    [fetchCategories],
  );

  const handleInlineCreateCategory = useCallback(
    async (data: ContactCategoryInput): Promise<ContactCategory> => {
      try {
        const created = await api.createContactCategory(data);
        toast({ description: `Category "${created.name}" created.` });
        await fetchCategories();
        return created;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Operation failed';
        toast({ title: 'Error', description: message, variant: 'destructive' });
        throw err;
      }
    },
    [fetchCategories],
  );

  const handleInlineCreatePriority = useCallback(
    async (data: ContactPriorityInput): Promise<ContactPriority> => {
      try {
        const created = await api.createContactPriority(data);
        toast({ description: `Priority "${created.name}" created.` });
        await fetchPriorities();
        return created;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Operation failed';
        toast({ title: 'Error', description: message, variant: 'destructive' });
        throw err;
      }
    },
    [fetchPriorities],
  );

  const handleDeleteCategory = useCallback((category: ContactCategory) => {
    setDeletingCategory(category);
    setDeleteCategoryDialogOpen(true);
  }, []);

  const handleConfirmDeleteCategory = useCallback(async () => {
    if (!deletingCategory) return;
    try {
      await api.deleteContactCategory(deletingCategory.id);
      toast({ description: 'Category deleted.' });
      setDeleteCategoryDialogOpen(false);
      if (selectedCategoryId === deletingCategory.id) {
        handleCategorySelect(null);
      }
      setDeletingCategory(null);
      await Promise.all([fetchCategories(), fetchContacts()]);
    } catch (err) {
      toast({
        title: 'Failed to delete category',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [deletingCategory, selectedCategoryId, handleCategorySelect, fetchCategories, fetchContacts]);

  // Category drag-to-reorder
  const [dragCatIdx, setDragCatIdx] = useState<number | null>(null);

  const handleCatDragOver = useCallback(
    (e: React.DragEvent, overIdx: number) => {
      e.preventDefault();
      if (dragCatIdx === null || dragCatIdx === overIdx) return;
      setCategories((prev) => {
        const next = [...prev];
        const [moved] = next.splice(dragCatIdx, 1);
        next.splice(overIdx, 0, moved);
        return next;
      });
      setDragCatIdx(overIdx);
    },
    [dragCatIdx],
  );

  const handleCatDragEnd = useCallback(async () => {
    setDragCatIdx(null);
    try {
      await api.reorderContactCategories(categories.map((c) => c.id));
    } catch {
      toast({ title: 'Failed to reorder categories', variant: 'destructive' });
      await fetchCategories();
    }
  }, [categories, fetchCategories]);

  // View switching
  const handleViewChange = useCallback(
    (newView: string) => {
      const v = newView as 'category' | 'date';
      setView(v);
      updateSettings({ defaultView: v });
    },
    [updateSettings],
  );

  // Date-grouped contacts
  const dateGroups = useMemo(() => {
    if (view !== 'date') return null;
    return groupContactsByDate(contacts);
  }, [contacts, view]);

  // Uncategorized count: total active contacts minus those in categories
  const uncategorizedCount = useMemo(() => {
    const categorizedTotal = categories.reduce((sum, cat) => sum + (cat.contactCount || 0), 0);
    return Math.max(totalContacts - categorizedTotal, 0);
  }, [totalContacts, categories]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Category Sidebar (visible in category view) */}
      {view === 'category' && (
        <div className="w-64 border-r flex-shrink-0 flex flex-col">
          <div className="p-3 border-b">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                setEditingCategory(null);
                setCategoryDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              New Category
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {/* All Contacts */}
            <button
              onClick={() => handleCategorySelect(null)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between transition-colors ${
                selectedCategoryId === null && selectedPriorityId === null
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-accent/50'
              }`}
            >
              <span className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                All Contacts
              </span>
              <span className="text-xs text-muted-foreground">{totalContacts}</span>
            </button>

            {/* Categories collapsible section */}
            <div>
              <div className="flex items-center">
                <button
                  onClick={() => setCategoriesExpanded(!categoriesExpanded)}
                  aria-expanded={categoriesExpanded}
                  className="flex-1 text-left px-3 py-2 rounded-md text-sm flex items-center transition-colors hover:bg-accent/50"
                >
                  <span className="flex items-center gap-2 text-muted-foreground font-medium">
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${categoriesExpanded ? '' : '-rotate-90'}`}
                    />
                    Categories
                  </span>
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-foreground"
                  title="Add New Category"
                  onClick={() => {
                    setEditingCategory(null);
                    setCategoryDialogOpen(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>

              {categoriesExpanded && (
                <div className="space-y-0.5 mt-0.5 pl-4">
                  {/* Uncategorized */}
                  <button
                    onClick={() => handleCategorySelect('uncategorized')}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between transition-colors ${
                      selectedCategoryId === 'uncategorized'
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent/50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <FolderOpen className="h-4 w-4" />
                      Uncategorized
                    </span>
                    <span className="text-xs text-muted-foreground mr-6">{uncategorizedCount}</span>
                  </button>

                  {/* User categories */}
                  {categories.map((cat, idx) => (
                    <div
                      key={cat.id}
                      draggable
                      onDragStart={() => setDragCatIdx(idx)}
                      onDragOver={(e) => handleCatDragOver(e, idx)}
                      onDragEnd={handleCatDragEnd}
                      className={`group relative ${dragCatIdx === idx ? 'opacity-50' : ''}`}
                    >
                      <button
                        onClick={() => handleCategorySelect(cat.id)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between transition-colors ${
                          selectedCategoryId === cat.id
                            ? 'bg-accent text-accent-foreground'
                            : 'hover:bg-accent/50'
                        }`}
                      >
                        <span className="flex items-center gap-2 truncate">
                          <GripVertical className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 cursor-grab active:cursor-grabbing" />
                          <span
                            className="h-3 w-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: cat.color }}
                          />
                          <span className="truncate">{cat.name}</span>
                        </span>
                        <span className="text-xs text-muted-foreground mr-6">
                          {cat.contactCount ?? 0}
                        </span>
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute -right-1 top-1/2 -translate-y-1/2 h-6 w-6 opacity-0 group-hover:opacity-100"
                          >
                            <MoreVertical className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setEditingCategory(cat);
                              setCategoryDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteCategory(cat)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Priorities collapsible section */}
            <div>
              <div className="flex items-center">
                <button
                  onClick={() => setPrioritiesExpanded(!prioritiesExpanded)}
                  aria-expanded={prioritiesExpanded}
                  className="flex-1 text-left px-3 py-2 rounded-md text-sm flex items-center transition-colors hover:bg-accent/50"
                >
                  <span className="flex items-center gap-2 text-muted-foreground font-medium">
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${prioritiesExpanded ? '' : '-rotate-90'}`}
                    />
                    Priorities
                  </span>
                </button>
              </div>

              {prioritiesExpanded && (
                <div className="space-y-0.5 mt-0.5 pl-4">
                  {/* No Priority */}
                  <button
                    onClick={() => handlePrioritySelect('none')}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between transition-colors ${
                      selectedPriorityId === 'none'
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent/50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <Flag className="h-4 w-4" />
                      No Priority
                    </span>
                  </button>

                  {/* User priorities */}
                  {priorities.map((prio) => (
                    <button
                      key={prio.id}
                      onClick={() => handlePrioritySelect(prio.id)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between transition-colors ${
                        selectedPriorityId === prio.id
                          ? 'bg-accent text-accent-foreground'
                          : 'hover:bg-accent/50'
                      }`}
                    >
                      <span className="flex items-center gap-2 truncate">
                        <span
                          className="h-3 w-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: prio.color }}
                        />
                        <span className="truncate">{prio.name}</span>
                      </span>
                      <span className="text-xs text-muted-foreground mr-6">
                        {prio.contactCount ?? 0}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Deleted Contacts (Trash) */}
            <div className="mt-4 pt-3 border-t">
              <button
                onClick={() => handleCategorySelect('__deleted__')}
                className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between transition-colors ${
                  selectedCategoryId === '__deleted__'
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent/50'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  Deleted Contacts
                </span>
                {trashCount > 0 && (
                  <span className="text-xs text-muted-foreground">{trashCount}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center justify-end gap-2">
            <Button
              onClick={() => {
                setEditingContact(null);
                setContactDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Contact
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="More actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setImportOpen(true)}>Import…</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setExportOpen(true)}>Export…</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setCustomFieldsOpen(true)}>
                  Custom Fields…
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setDuplicatesOpen(true)}>
                  Find Duplicates…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon" onClick={() => setSettingsOpen(true)}>
              <Settings className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Search contacts..."
                className="pl-9 pr-8"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <Tabs value={view} onValueChange={handleViewChange}>
              <TabsList>
                <TabsTrigger value="category">
                  <Tag className="h-4 w-4 mr-1" />
                  Categories
                </TabsTrigger>
                <TabsTrigger value="date">
                  <Calendar className="h-4 w-4 mr-1" />
                  Timeline
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Contact List */}
        <div className="flex-1 overflow-y-auto p-4">
          {isTrashView && contacts.length > 0 && (
            <div className="flex items-center justify-between mb-4 pb-3 border-b">
              <p className="text-sm text-muted-foreground">
                {contacts.length} contact{contacts.length !== 1 ? 's' : ''} in trash
              </p>
              <Button variant="destructive" size="sm" onClick={() => setEmptyTrashDialogOpen(true)}>
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Empty Trash
              </Button>
            </div>
          )}

          {contacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              {isTrashView ? (
                <>
                  <Trash2 className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <h3 className="font-medium">Trash is empty</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Deleted contacts will appear here.
                  </p>
                </>
              ) : (
                <>
                  <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  {search ? (
                    <>
                      <h3 className="font-medium">No contacts match &quot;{search}&quot;</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Try a different search term.
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="font-medium">No contacts yet</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Add your first contact to get started.
                      </p>
                      <Button
                        className="mt-4"
                        onClick={() => {
                          setEditingContact(null);
                          setContactDialogOpen(true);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Contact
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          ) : view === 'date' && dateGroups && !isTrashView ? (
            <div className="space-y-6">
              {Array.from(dateGroups.entries()).map(([group, groupContacts]) => (
                <div key={group}>
                  <h2 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {group}
                    <span className="text-xs font-normal">({groupContacts.length})</span>
                  </h2>
                  <div className="grid gap-2">
                    {groupContacts.map((contact) => (
                      <ContactCard
                        key={contact.id}
                        contact={contact}
                        onEdit={handleEditContact}
                        onShare={handleShareContact}
                        onDelete={handleDeleteContact}
                        onArchive={handleArchiveContact}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-2">
              {contacts.map((contact) => (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  onEdit={handleEditContact}
                  onShare={handleShareContact}
                  onDelete={handleDeleteContact}
                  onArchive={handleArchiveContact}
                  onRestore={handleRestoreContact}
                  onPermanentDelete={handlePermanentDelete}
                  isTrashView={isTrashView}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <ContactFormDialog
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
        contact={editingContact}
        categories={categories}
        priorities={priorities}
        onSave={handleSaveContact}
        onCreateCategory={handleInlineCreateCategory}
        onCreatePriority={handleInlineCreatePriority}
      />

      <CategoryDialog
        open={categoryDialogOpen}
        onOpenChange={setCategoryDialogOpen}
        category={editingCategory}
        onSave={handleSaveCategory}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        contact={deletingContact}
        onConfirm={handleConfirmDelete}
      />

      <EmptyTrashDialog
        open={emptyTrashDialogOpen}
        onOpenChange={setEmptyTrashDialogOpen}
        count={trashCount}
        onConfirm={handleEmptyTrash}
      />

      {/* Permanent Delete Confirmation */}
      <Dialog open={permanentDeleteDialogOpen} onOpenChange={setPermanentDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Permanently Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete{' '}
              {[permanentDeletingContact?.firstName, permanentDeletingContact?.lastName]
                .filter(Boolean)
                .join(' ')}
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermanentDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmPermanentDelete}>
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation */}
      <Dialog open={deleteCategoryDialogOpen} onOpenChange={setDeleteCategoryDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
            <DialogDescription>
              Delete category &quot;{deletingCategory?.name}&quot;?
              {deletingCategory?.contactCount && deletingCategory.contactCount > 0
                ? ` ${deletingCategory.contactCount} contact(s) will become uncategorized.`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCategoryDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDeleteCategory}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Sheet */}
      <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Settings</SheetTitle>
            <SheetDescription>Configure Contact Book preferences.</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-6">
            <div className="space-y-2">
              <Label>Default View</Label>
              <Select
                value={settings.defaultView}
                onValueChange={(v) => updateSettings({ defaultView: v as 'category' | 'date' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="category">Categories</SelectItem>
                  <SelectItem value="date">Timeline</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Sort By</Label>
              <Select
                value={settings.sortBy}
                onValueChange={(v) => {
                  updateSettings({ sortBy: v as ContactBookSettings['sortBy'] });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gatheredAt">Date Gathered</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                  <SelectItem value="createdAt">Date Added</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Sort Direction</Label>
              <Select
                value={settings.sortDir}
                onValueChange={(v) => updateSettings({ sortDir: v as 'asc' | 'desc' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {settings.sortBy === 'name' || settings.sortBy === 'company' ? (
                    <>
                      <SelectItem value="asc">A → Z</SelectItem>
                      <SelectItem value="desc">Z → A</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="desc">Newest First</SelectItem>
                      <SelectItem value="asc">Oldest First</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <ManageCustomFieldsDialog
        open={customFieldsOpen}
        onOpenChange={setCustomFieldsOpen}
        fields={customFields}
        onFieldsChanged={fetchCustomFields}
      />

      <DuplicatesDialog
        open={duplicatesOpen}
        onOpenChange={setDuplicatesOpen}
        sensitivity={settings.duplicateSensitivity}
        onComplete={fetchContacts}
      />

      <ShareDialog
        open={sharingContact !== null}
        onOpenChange={(open) => {
          if (!open) setSharingContact(null);
        }}
        contact={sharingContact}
      />

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        currentCategoryId={selectedCategoryId}
        currentPriorityId={selectedPriorityId}
        currentSearch={search}
      />

      <ContactImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImportComplete={() => {
          fetchContacts();
          fetchCategories();
        }}
      />
    </div>
  );
}
